import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Eye, Image, Plus, Upload, X, Images, Code, Type } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useSite } from '../../contexts/SiteContext';
import { ImageGalleryModal } from '../../components/ImageGalleryModal';
import { MarkdownHelpModal } from '../../components/MarkdownHelpModal';
import { InsertDiagramMenu } from '../../components/InsertDiagramMenu';
import { WysiwygEditor, computeImageStyle } from '../../components/WysiwygEditor';
import { DraftPreview } from '../../components/DraftPreview';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PageEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { refreshPages } = useSite();
  const contentInputRef = useRef(null);
  const contentImageInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const editorRef = useRef(null);
  const isEditing = !!slug;

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    cover_image: '',
    show_in_menu: true,
    menu_order: 0,
    menu_group: '',
    menu_section: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [pendingImageUrl, setPendingImageUrl] = useState('');
  const [imageSize, setImageSize] = useState('medium');
  const [imageAlign, setImageAlign] = useState('center');
  const [editorMode, setEditorMode] = useState('wysiwyg');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isEditing) {
      fetchPage();
    }
  }, [slug]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pages/${slug}`, { withCredentials: true });
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load page');
      navigate('/admin/pages');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate slug from title
    if (name === 'title' && !isEditing) {
      const generatedSlug = value
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-');
      setFormData(prev => ({ ...prev, slug: generatedSlug }));
    }
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, cover_image: response.data.url }));
      toast.success('Cover image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleContentImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPendingImageUrl(response.data.url);
      setImageDialogOpen(true);
      toast.success('Image uploaded - now set size and position');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleGallerySelect = (imageData) => {
    if (editorMode === 'wysiwyg' && editorRef.current) {
      editorRef.current.insertImage(imageData.url, imageData.size, imageData.align);
    } else {
      const imageTag = `\n\n[IMG:${imageData.url}|size:${imageData.size}|align:${imageData.align}]\n\n`;
      const textarea = contentInputRef.current;
      const cursorPos = textarea?.selectionStart || formData.content.length;
      const newContent = 
        formData.content.slice(0, cursorPos) + 
        imageTag + 
        formData.content.slice(cursorPos);
      setFormData(prev => ({ ...prev, content: newContent }));
    }
  };

  const insertDiagram = (tpl) => {
    if (editorMode === 'wysiwyg' && editorRef.current?.insertMermaid) {
      editorRef.current.insertMermaid(tpl.code);
    } else {
      const block = `\n\n\`\`\`mermaid\n${tpl.code}\n\`\`\`\n\n`;
      const textarea = contentInputRef.current;
      const cursorPos = textarea?.selectionStart ?? formData.content.length;
      const newContent =
        formData.content.slice(0, cursorPos) + block + formData.content.slice(cursorPos);
      setFormData(prev => ({ ...prev, content: newContent }));
      setTimeout(() => textarea?.focus(), 0);
    }
    toast.success(`${tpl.label} diagram inserted`);
  };

  const insertImageIntoContent = () => {
    if (!pendingImageUrl) return;

    const sizeMap = {
      small: '25%',
      medium: '50%',
      large: '75%',
      full: '100%'
    };

    const fullUrl = pendingImageUrl.startsWith('/api') 
      ? `${process.env.REACT_APP_BACKEND_URL}${pendingImageUrl}` 
      : pendingImageUrl;

    const size = sizeMap[imageSize];

    if (editorMode === 'wysiwyg' && editorRef.current) {
      editorRef.current.insertImage(fullUrl, size, imageAlign);
    } else {
      const imageTag = `\n\n[IMG:${fullUrl}|size:${size}|align:${imageAlign}]\n\n`;
      const textarea = contentInputRef.current;
      const cursorPos = textarea?.selectionStart || formData.content.length;
      const newContent = 
        formData.content.slice(0, cursorPos) + 
        imageTag + 
        formData.content.slice(cursorPos);
      setFormData(prev => ({ ...prev, content: newContent }));
    }

    setImageDialogOpen(false);
    setPendingImageUrl('');
    setImageSize('medium');
    setImageAlign('center');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.slug || !formData.content) {
      toast.error('Please fill in title, slug, and content');
      return;
    }

    // Clean HTML before saving: strip <p> wrappers from inside <li>
    const cleanContent = formData.content.replace(/<li><p>([\s\S]*?)<\/p><\/li>/gi, '<li>$1</li>');
    const saveData = { ...formData, content: cleanContent };

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/pages/${slug}`, saveData, { withCredentials: true });
        toast.success('Page updated');
      } else {
        await axios.post(`${API}/pages`, saveData, { withCredentials: true });
        toast.success('Page created');
      }
      refreshPages();
      navigate('/admin/pages');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-64 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl" data-testid="page-editor">
      <div className="mb-8">
        <Link 
          to="/admin/pages"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to pages
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">
          {isEditing ? 'Edit Page' : 'New Page'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Page title"
              data-testid="page-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug *</Label>
            <Input
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              required
              placeholder="page-url-slug"
              data-testid="page-slug-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label htmlFor="content">Content *</Label>
              {editorMode === 'markdown' && <MarkdownHelpModal />}
              <InsertDiagramMenu onInsert={insertDiagram} />
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(true)}
                data-testid="preview-btn"
              >
                <Eye className="mr-2 h-4 w-4" />
                Preview
              </Button>

              <div className="flex border border-input rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setEditorMode('wysiwyg')}
                  className={`px-2 py-1 text-xs flex items-center gap-1 transition-colors ${
                    editorMode === 'wysiwyg'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid="mode-wysiwyg-btn"
                >
                  <Type className="h-3 w-3" />
                  Visual
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('markdown')}
                  className={`px-2 py-1 text-xs flex items-center gap-1 transition-colors ${
                    editorMode === 'markdown'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid="mode-markdown-btn"
                >
                  <Code className="h-3 w-3" />
                  Markdown
                </button>
              </div>

              <input
                type="file"
                ref={contentImageInputRef}
                onChange={handleContentImageUpload}
                accept="image/*"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setGalleryOpen(true)}
                data-testid="gallery-btn"
              >
                <Images className="mr-2 h-4 w-4" />
                Gallery
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => contentImageInputRef.current?.click()}
                disabled={uploading}
                data-testid="insert-image-btn"
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </Button>
            </div>
          </div>

          {editorMode === 'wysiwyg' ? (
            <WysiwygEditor
              ref={editorRef}
              content={formData.content}
              onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
              placeholder="Start writing your page content..."
            />
          ) : (
            <>
              <Textarea
                ref={contentInputRef}
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                placeholder="Write your page content here..."
                rows={15}
                className="font-mono text-sm"
                data-testid="page-content-input"
              />
              <p className="text-xs text-muted-foreground">
                Markdown mode — paste or write Markdown (headings, tables, lists, images, diagrams). Raw HTML also works. Switch to Visual for rich-text editing.
              </p>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center gap-3 p-4 border border-border rounded-lg">
            <Switch
              id="show_in_menu"
              checked={formData.show_in_menu}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_in_menu: checked }))}
              data-testid="page-menu-switch"
            />
            <Label htmlFor="show_in_menu" className="cursor-pointer">
              <span className="font-medium">Show in Menu</span>
              <p className="text-sm text-muted-foreground">
                Display this page in the navigation menu
              </p>
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="menu_order">Menu Order</Label>
            <Input
              type="number"
              id="menu_order"
              name="menu_order"
              value={formData.menu_order}
              onChange={handleChange}
              min="0"
              placeholder="0"
              data-testid="page-order-input"
            />
            <p className="text-xs text-muted-foreground">
              Lower numbers appear first in the menu
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="menu_group">Menu Group (optional)</Label>
          <Input
            id="menu_group"
            name="menu_group"
            value={formData.menu_group || ''}
            onChange={handleChange}
            placeholder="e.g. Projects"
            data-testid="page-group-input"
          />
          <p className="text-xs text-muted-foreground">
            Pages sharing the same group name are collapsed into a single dropdown menu.
            Leave blank to show as a top-level link.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="menu_section">Sub-section (optional)</Label>
          <Input
            id="menu_section"
            name="menu_section"
            value={formData.menu_section || ''}
            onChange={handleChange}
            placeholder="e.g. Solar, Electronics, Tech"
            data-testid="page-section-input"
          />
          <p className="text-xs text-muted-foreground">
            Nests this page one level deeper inside its Menu Group. Example: Group “Projects”
            + Sub-section “Solar” shows as Projects ▸ Solar ▸ this page. Requires a Menu Group.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Cover Image (optional)</Label>
          <div className="flex items-center gap-4">
            <input
              type="file"
              ref={coverInputRef}
              onChange={handleCoverImageUpload}
              accept="image/*"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => coverInputRef.current?.click()}
              disabled={uploading}
              data-testid="page-cover-upload-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
            {formData.cover_image && (
              <div className="flex items-center gap-2">
                <img
                  src={formData.cover_image.startsWith('/api')
                    ? `${process.env.REACT_APP_BACKEND_URL}${formData.cover_image}`
                    : formData.cover_image}
                  alt="Cover preview"
                  className="h-12 w-12 object-cover rounded"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, cover_image: '' }))}
                  className="text-destructive hover:text-destructive/80"
                  data-testid="page-cover-remove-btn"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          <Input
            name="cover_image"
            value={formData.cover_image || ''}
            onChange={handleChange}
            placeholder="Or enter image URL..."
            data-testid="page-cover-url-input"
          />
          <p className="text-xs text-muted-foreground">
            Shown on the page's card in the “Browse Projects” index.
          </p>
        </div>

        <div className="flex items-center gap-4 pt-4">
          <Button type="submit" disabled={saving} data-testid="save-page-btn">
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : isEditing ? 'Update Page' : 'Create Page'}
          </Button>
          {isEditing && (
            <Link to={`/page/${slug}`} target="_blank">
              <Button type="button" variant="outline">
                <Eye className="mr-2 h-4 w-4" />
                View Page
              </Button>
            </Link>
          )}
        </div>
      </form>

      {/* Image Insert Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Image</DialogTitle>
            <DialogDescription>
              Choose the size and alignment for your image
            </DialogDescription>
          </DialogHeader>
          
          {pendingImageUrl && (
            <div className="my-4">
              <img
                src={pendingImageUrl.startsWith('/api')
                  ? `${process.env.REACT_APP_BACKEND_URL}${pendingImageUrl}`
                  : pendingImageUrl}
                alt="Preview"
                className="max-h-48 mx-auto rounded"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Size</Label>
              <Select value={imageSize} onValueChange={setImageSize}>
                <SelectTrigger data-testid="image-size-select">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small (25%)</SelectItem>
                  <SelectItem value="medium">Medium (50%)</SelectItem>
                  <SelectItem value="large">Large (75%)</SelectItem>
                  <SelectItem value="full">Full Width (100%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Alignment</Label>
              <Select value={imageAlign} onValueChange={setImageAlign}>
                <SelectTrigger data-testid="image-align-select">
                  <SelectValue placeholder="Select alignment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImageDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={insertImageIntoContent} data-testid="confirm-insert-image">
              <Plus className="mr-2 h-4 w-4" />
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelect={handleGallerySelect}
      />

      {/* Draft Preview */}
      {showPreview && (
        <DraftPreview
          post={{ ...formData, categories: [], tags: [] }}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
